import { Component, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import { ConsultasOrdenService } from '@services/orden-consultas/signo-orden-consultas.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';

@Component({
    selector: 'app-ordenes-periodo',
    templateUrl: './ordenes-periodo.component.html',
    styleUrls: ['./ordenes-periodo.component.scss'],
})
export class OrdenesPeriodoComponent implements OnInit, AfterViewInit {
    from = ''; // yyyy-MM-dd
    to = '';

    loading = false;
    error = '';

    total = 0;
    rows: any[] = [];
    dataSource = new MatTableDataSource<any>([]);
    displayedColumns = [
        'nro',
        'createdAt',
        'estado',
        'tipo',
        'sede',
        'afiliado',
    ];

    @ViewChild(MatPaginator) paginator!: MatPaginator;

    constructor(private srv: ConsultasOrdenService) {}

    ngOnInit(): void {
        // Precargo FECHAS por conveniencia (primer y último día del mes),
        // pero NO ejecuto la búsqueda automática.
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        this.from = this.toInputValue(first);
        this.to = this.toInputValue(last);
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        if (this.paginator) {
            this.paginator.pageSize = 20; // pagina de a 20
        }
    }

    private toInputValue(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    async buscar() {
        try {
            this.loading = true;
            this.error = '';
            this.rows = [];
            this.total = 0;

            const fromIso = this.from
                ? new Date(this.from + 'T00:00:00').toISOString()
                : '';
            const toIso = this.to
                ? new Date(this.to + 'T23:59:59').toISOString()
                : '';

            const data = await this.srv.listarPorPeriodo(fromIso, toIso);
            const list = (data || []) as any[];

            this.total = list.length;

            this.rows = list.map((o: any) => {
                const estado = o?.estado ?? o?.lastState?.state ?? '';
                const tipo = o?.tipo?.nombre ?? o?.tipo ?? '';
                const sede = o?.sede?.nombre ?? o?.sede ?? '';

                // usuario / afiliado formateado (preferencia: fullName, luego Apellido, Nombre)
                let afiliado = '';
                const u = o?.usuario || o?.afiliado;
                if (u) {
                    if (u.fullName) {
                        afiliado = u.fullName;
                    } else if (u.apellidoPersona || u.nombrePersona) {
                        afiliado = `${u.apellidoPersona || ''}, ${
                            u.nombrePersona || ''
                        }`
                            .trim()
                            .replace(/^,|,\s*$/g, '');
                    } else if (u.apellido || u.nombre) {
                        afiliado = `${u.apellido || ''}, ${u.nombre || ''}`
                            .trim()
                            .replace(/^,|,\s*$/g, '');
                    } else if (u.email) {
                        afiliado = u.email;
                    }
                }

                return {
                    _id: o._id,
                    codigo: o.codigo,
                    nro: o.codigo || o.nro || o._id,
                    createdAt: o.createdAt || o.issuedAt,
                    estado,
                    tipo,
                    sede,
                    afiliado,
                };
            });

            this.dataSource.data = this.rows;
            if (this.paginator) this.paginator.firstPage();
        } catch (e: any) {
            this.error = e?.message || 'No se pudo cargar';
            Swal.fire({ icon: 'error', title: 'Error', text: this.error });
        } finally {
            this.loading = false;
        }
    }

    exportarExcel() {
        if (!this.rows?.length) {
            Swal.fire({
                icon: 'info',
                title: 'Sin datos',
                text: 'No hay filas para exportar.',
            });
            return;
        }

        const plain = this.rows.map((r) => ({
            'Código / Nº': r.nro || r._id,
            Fecha: r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
            Estado: r.estado || '',
            Tipo: r.tipo || '',
            Sede: r.sede || '',
            'Usuario / Afiliado': r.afiliado || '',
        }));

        const ws = XLSX.utils.json_to_sheet(plain);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Órdenes');

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const fname = `ordenes_${this.from || 'inicio'}_${
            this.to || 'fin'
        }.xlsx`;
        saveAs(blob, fname);
    }
}
